import type {
  VaultProvider,
  VaultPublicDataAll,
} from '../../vaults/fetchVaultsAll'
import type { TermSheet } from '../types'
import { buildVaultTermSheet } from './build'
import { toVaultTermInput, type AnyVaultRow } from './input'

/**
 * Stamp `termSheet` onto every vault in a `VaultPublicDataAll` payload.
 *
 * Mirrors `stampVaultClassification` deliberately — same shape, same call site,
 * same mutate-in-place contract — so both the per-provider objects and the
 * lookup carry the field, and a consumer never has to ask which pass ran.
 *
 * Runs AFTER `stampVaultClassification`, because it reads `yieldProfile` (the
 * classification's output) to decide whether the principal can draw down.
 *
 * Every provider bag is walked, including the ones with thin data: a vault we
 * know little about still gets a sheet, and `coverage.pending` says what is
 * missing. Skipping those rows would leave a consumer unable to distinguish
 * "no sheet built" from "no terms to state".
 */
export function stampVaultTermSheets(
  data: VaultPublicDataAll,
  chainId: string | number,
): void {
  const chain = String(chainId)

  const stampBag = (
    bag: Record<string, AnyVaultRow> | undefined,
    provider: VaultProvider,
  ) => {
    if (!bag) return
    for (const vault of Object.values(bag)) {
      const input = toVaultTermInput(vault, provider, chain)
      if (!input) continue
      ;(vault as { termSheet?: TermSheet }).termSheet =
        buildVaultTermSheet(input)
    }
  }

  stampBag(data.fluid, 'fluid')
  stampBag(data.gearbox, 'gearbox')
  stampBag(data.morpho, 'morpho')
  stampBag(data.lista, 'lista')
  stampBag(data.silo, 'silo')
  stampBag(data['euler-earn'], 'euler-earn')
  stampBag(data.termmax, 'termmax')
  stampBag(data.lst, 'lst')
  stampBag(data.savings, 'savings')
  stampBag(data.lagoon, 'lagoon')
  stampBag(data['aave-earn'], 'aave-earn')
  stampBag(data.upshift, 'upshift')
  stampBag(data.yearn, 'yearn')
  stampBag(data.pendle, 'pendle')
  stampBag(data.spectra, 'spectra')
  stampBag(data.hypercore, 'hypercore')
  stampBag(data.gmx, 'gmx')
}
